import levenshtein from 'fast-levenshtein';

const keywords = [
  'Henvisningsårsag',
  'Anamnese',
  'Allergier',
  'Dispositioner',
  'Tidligere',
  'Øvrige Lidelser',
  'Aktuelt medicin',
  'Kram',
  'Socialt',
  'Objektivt',
  'Konklusion',
  'Plan',
  'Aktuelle',
  'Tidligere Gyn',
  'Expositioner',
  'Indikation',
  'Henvisende læge',
  'Undersøgelsesdato',
];

const addZeroWidthSpaces = (text: string) => {
  text = text.replaceAll(/^\n$/g, '&#x200b;\n&#x200b;');
  text = text.replaceAll(/\n$/g, '\n&#x200b;');
  text = text.replaceAll(/^\n/g, '&#x200b;\n');
  return text;
};

const LETTER = '[A-Za-zÆØÅæøå]';
// Verbal fillers: øh, øhh, øhm (and elongated variants like øhhm)
const FILLER = 'øh+m?';
export const removeFillers = (text: string): string =>
  text
    // at start of string or line: filler + optional punctuation + horizontal whitespace
    .replace(new RegExp(`(?:^|(?<=\\n))[^\\S\\n]*(?:${FILLER})(?!${LETTER})[.,!?]*[^\\S\\n]*`, 'gi'), '')
    // mid-line: preceding horizontal whitespace + filler + optional punctuation
    .replace(new RegExp(`[^\\S\\n]+(?:${FILLER})(?!${LETTER})[.,!?]*(?=\\s|$)`, 'gi'), '')
    .replace(/ {2,}/g, ' ');

export const cleanText = (text: string): string => {
  text = text
    .replace(/&#x20;/g, ' ')
    .trim()
    .replace(/&#x200b;/g, '')
    .trim();
  text = removeFillers(text);
  if (text.trim().length === 0) {
    return '&#x20;';
  }
  return text;
};

const prepareKeyword = (keyword: string) =>
  keyword.toLocaleLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '');

// fuzzyFixToBoldKeywords performs fuzzy matching on the text to identify 'nyt afsnit' commands.
// It fuzzy matches with a distance of 1 for keywords 5 chars and below, 2 for those above
// If a match is found, it replaces with the correct keyword and  bolds it
const fuzzyFixToBoldKeywords = (text: string) =>
  text
    .split('\n\n')
    .map((section: string, index: number) => {
      if (index === 0) return section;
      let found = false;

      for (let keyword of keywords) {
        const transcribedKeyword = section.slice(0, keyword.length);

        const distance = levenshtein.get(
          prepareKeyword(keyword),
          prepareKeyword(transcribedKeyword),
          { useCollator: true },
        );

        const maxDistance = keyword.length <= 5 ? 1 : 2;
        if (distance <= maxDistance) {
          const regex = new RegExp(`^(${transcribedKeyword})[,. ]{0,2}`, 'i');
          if (regex.test(section)) {
            found = true;
            const subsequentText = section.slice(keyword.length).replace(/^[,. ]{0,2}/, '');
            return `\n\n**${keyword}**\n${subsequentText}`;
          }
        }
      }
      return found ? section : `\n\n${section}`;
    })
    .join('');

const formatText = (newSnippet: string) => {
  // Use consistent line breaks
  let formattedText = newSnippet.replaceAll('<br />', '\n');
  formattedText = removeFillers(formattedText);
  // Support 'nyt afsnit' commands
  formattedText = fuzzyFixToBoldKeywords(formattedText);
  // Prevent markdown new lines from being collapsed
  formattedText = addZeroWidthSpaces(formattedText);
  // Add tab before "Rp."
  formattedText = formattedText.replaceAll('Rp.', '\tRp.');
  return formattedText;
};

export const fixMarkdownText = (input: string) => {
  // fixes user error with colons (not an issue once we always use the LLM fixer)
  input = input.replaceAll(/(?<!:):(?!(::|::note|\s))/g, ': ');
  input = input.replaceAll('```', '``');
  // fixes an over-aggressive replacement above that breaks markdown
  input = input.replaceAll(': **', ':**');
  return input;
};

export default formatText;
